/**
 * Installation Bundle
 * Auto-adds installation product to cart when an install-required product is added.
 *
 * Uses a MutationObserver on the add-to-cart button's "added" animation class
 * to detect successful add-to-cart, avoiding any fetch monkey-patching.
 * After adding installation, forces a full cart section re-render.
 */
(function () {
  'use strict';

  function getInstallVariantId() {
    var el = document.querySelector('[data-install-variant-id]');
    return el ? el.dataset.installVariantId : null;
  }

  /**
   * Fetch /cart.js with retry-on-429 backoff.
   * Returns the cart object, or null if Shopify keeps rate-limiting us.
   * Added 2026-05-20 because Shopify storefront /cart.js was returning 429s under
   * combined load (pixel script + apps + this script), and the resulting HTML
   * rate-limit page broke JSON parsing.
   */
  async function fetchCartJsonWithBackoff(attempt) {
    attempt = attempt || 0;
    try {
      var resp = await fetch('/cart.js');
      if (resp.status === 429) {
        if (attempt >= 2) return null;
        var delay = 1500 * Math.pow(2, attempt); // 1.5s, 3s, 6s
        await new Promise(function (r) { setTimeout(r, delay); });
        return fetchCartJsonWithBackoff(attempt + 1);
      }
      return await resp.json();
    } catch (e) {
      return null;
    }
  }

  /**
   * Add installation product and refresh the entire cart UI
   */
  async function addInstallationToCart(installVariantId) {
    try {
      // First check if installation is already in cart
      var cartData = await fetchCartJsonWithBackoff(0);
      if (!cartData || !cartData.items) {
        console.warn('[InstallBundle] Cart fetch failed (likely 429); skipping installation auto-add');
        return;
      }
      var alreadyInCart = cartData.items.some(function (item) {
        return String(item.variant_id) === String(installVariantId);
      });

      if (alreadyInCart) {
        console.log('[InstallBundle] Installation already in cart, skipping');
        return;
      }

      // Add installation product
      var resp = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{
            id: parseInt(installVariantId, 10),
            quantity: 1,
            properties: {
              '_bundled_with': 'Outdoor Kitchen Package',
              '_type': 'Installation'
            }
          }]
        })
      });

      if (!resp.ok) {
        console.warn('[InstallBundle] Failed to add installation:', resp.status);
        return;
      }

      console.log('[InstallBundle] Installation added to cart');

      // Force the theme to re-render all cart sections so line numbers are correct
      // Find all cart-items-component elements and trigger a section re-render
      refreshCartUI();

    } catch (err) {
      console.warn('[InstallBundle] Error:', err);
    }
  }

  /**
   * Refresh the cart UI by re-fetching the current page with section rendering
   * This ensures the cart knows about the installation line item
   */
  function refreshCartUI() {
    var cartComponents = document.querySelectorAll('cart-items-component');

    if (cartComponents.length === 0) {
      // No cart components visible (e.g. mini cart not open yet) — that's fine,
      // it will load fresh when opened
      return;
    }

    // Collect section IDs from cart components
    var sectionIds = [];
    cartComponents.forEach(function (el) {
      if (el.dataset && el.dataset.sectionId) {
        sectionIds.push(el.dataset.sectionId);
      }
    });

    if (sectionIds.length === 0) return;

    // Use Shopify's Section Rendering API to get fresh cart HTML
    var sectionsParam = sectionIds.map(function (id) {
      return 'sections=' + encodeURIComponent(id);
    }).join('&');

    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        // Dispatch a custom event that mimics what the theme expects
        // This triggers cart-items-component to re-render
        var event = new CustomEvent('cart:add', {
          bubbles: true,
          detail: {
            id: 'installation-bundle',
            data: {
              itemCount: cart.item_count,
              source: 'installation-bundle'
            }
          }
        });
        document.dispatchEvent(event);

        // Also try section rendering approach
        fetch(window.location.pathname + '?sections=' + sectionIds.join(','))
          .then(function (r) { return r.json(); })
          .then(function (sections) {
            // Morph each section with fresh HTML
            sectionIds.forEach(function (id) {
              if (sections[id]) {
                var parser = new DOMParser();
                var newDoc = parser.parseFromString(sections[id], 'text/html');
                var newContent = newDoc.querySelector('cart-items-component');
                var existing = document.querySelector('cart-items-component[data-section-id="' + id + '"]');
                if (existing && newContent) {
                  existing.innerHTML = newContent.innerHTML;
                }
              }
            });
          })
          .catch(function () {
            // Fallback: just reload if section rendering fails
          });
      });
  }

  function init() {
    var installVariantId = getInstallVariantId();
    if (!installVariantId) return;

    // Watch for the theme's "atc-added" class which is added on successful add-to-cart
    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        if (mutation.type !== 'attributes' || mutation.attributeName !== 'class') return;

        var target = mutation.target;
        if (target.classList && target.classList.contains('atc-added')) {
          // Successful add-to-cart detected — add installation after a short delay
          // to let the theme finish processing
          setTimeout(function () {
            addInstallationToCart(installVariantId);
          }, 800);
        }
      });
    });

    // Observe all add-to-cart buttons for the "atc-added" class change
    var buttons = document.querySelectorAll('.add-to-cart-button, [ref="addToCartButton"]');
    buttons.forEach(function (btn) {
      observer.observe(btn, { attributes: true, attributeFilter: ['class'] });
    });

    // Also observe dynamically added buttons (e.g. after variant change)
    var productForm = document.querySelector('product-form-component');
    if (productForm) {
      var formObserver = new MutationObserver(function () {
        var newButtons = document.querySelectorAll('.add-to-cart-button, [ref="addToCartButton"]');
        newButtons.forEach(function (btn) {
          observer.observe(btn, { attributes: true, attributeFilter: ['class'] });
        });
      });
      formObserver.observe(productForm, { childList: true, subtree: true });
    }

    console.log('[InstallBundle] Ready — watching for add-to-cart, variant:', installVariantId);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
